import { gridConfig } from '@tvg/fuse-design/responsive-grid';
import { TypographyComponent as Typography } from '@tvg/fuse-design/typography-element';
import SrOnly from '@tvg/fuse-utils/lib/sr-only';
import { PLPQueryData, usePLPQuery } from 'client/api/plp-results';
import { DEFAULT_PRODUCTS_PER_PAGE } from 'common/config';
import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';

const DivStyled = styled.div`
  & {
    padding-top: var(--spacing-space16);
    padding-bottom: var(--spacing-space16);
    margin-right: auto;

    @media screen and (min-width: ${gridConfig.m.minWidth}) {
      padding-top: var(--spacing-space4);
      padding-bottom: var(--spacing-space4);
    }

    @media screen and (min-width: ${gridConfig.l.minWidth}) {
      padding-top: 0;
      padding-bottom: 0;
      margin-right: 0;
    }
  }

  &#results-header-bottom {
    margin-right: auto;
  }
`;

type ResultCountProps = {
  position: string;
};

function ResultCount({ position }: ResultCountProps) {
  const { data } = usePLPQuery(
    useCallback(
      (d: PLPQueryData) => ({
        numResults: d.numResults,
        resultsPerPage: d.resultsPerPage,
        page: d.page,
      }),
      [],
    ),
  );

  const numResults = data?.numResults ?? 0;
  const page = data?.page ?? 1;
  const resultsPerPage = data?.resultsPerPage ?? DEFAULT_PRODUCTS_PER_PAGE;

  const id = `results-header-${position}`;
  const isTopComponent = position === 'top';

  const text = useMemo(() => {
    if (numResults === undefined || page === undefined || resultsPerPage === undefined) {
      return { resultCount: 'Results:', screenReader: '' };
    }

    const startProductNumber = 1 + (page - 1) * resultsPerPage;
    const endProductNumber = page * resultsPerPage;
    const endProduct = endProductNumber < numResults ? endProductNumber : numResults;
    const displayedResultCount: string | number | undefined = numResults <= 9999 ? numResults : '10k+';

    const resultCountText = `Results: ${startProductNumber} - ${endProduct} (of ${displayedResultCount})`;
    return {
      resultCount: resultCountText,
      screenReader: `Page ${page}. ${resultCountText.replace('-', 'to')}`,
    };
  }, [numResults, page, resultsPerPage]);

  const textSizes = useMemo(() => {
    if (isTopComponent) return { xxs: '1', m: '2', l: '4' };
    return { xxs: '1', m: '1', l: '1' };
  }, [isTopComponent]);

  return (
    <DivStyled id={id} aria-live={isTopComponent ? 'polite' : 'off'} role={isTopComponent ? 'alert' : undefined}>
      <SrOnly>{text.screenReader}</SrOnly>
      <Typography dataTestId="results-count" tag="h2" size={textSizes} type="heading" aria-hidden>
        {text.resultCount}
      </Typography>
    </DivStyled>
  );
}

export default React.memo(ResultCount);
